/**
 * SSE Service for Frontend
 * Manages EventSource connections for real-time Server-Sent Events
 */

export interface ProcessingEventData {
  sessionId: string;
  documentId: string;
  eventType: 'initialization' | 'chunking' | 'embedding' | 'storage' | 'complete' | 'error';
  stage: string;
  progress: number;
  message: string;
  metadata: {
    chunkIndex?: number;
    chunkText?: string;
    chunkSize?: number;
    embeddingDimensions?: number;
    totalChunks?: number;
    processingTime?: number;
    error?: string;
    [key: string]: any;
  };
  timestamp: string;
}

export interface SSECallbacks {
  onEvent?: (event: ProcessingEventData) => void;
  onConnect?: () => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

class SSEService {
  private connections: Map<string, EventSource>;
  private reconnectAttempts: Map<string, number>;
  private readonly RECONNECT_DELAY = 3000; // 3 seconds
  private readonly MAX_RECONNECT_ATTEMPTS = 2; // Reduced to prevent spam

  constructor() {
    this.connections = new Map();
    this.reconnectAttempts = new Map();
  }

  /**
   * Connect to SSE stream
   */
  connect(
    documentId: string,
    sessionId: string,
    callbacks: SSECallbacks
  ): EventSource {
    const connectionKey = `${documentId}_${sessionId}`;

    // Close existing connection if any
    this.disconnect(connectionKey);

    // Get API base URL from environment or use proxy
    // If REACT_APP_API_URL is set and includes /api, don't add it again
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    const baseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;
    const url = `${baseUrl}/api/documents/${documentId}/rag/events/${sessionId}`;

    console.log(`[SSE] Connecting to: ${url}`);

    const eventSource = new EventSource(url);
    
    console.log(`[SSE] EventSource created, readyState: ${eventSource.readyState}`);
    console.log(`[SSE] Setting up event listeners...`);

    // Handle incoming events - SET UP BEFORE onopen!
    eventSource.addEventListener('event', (e: MessageEvent) => {
      console.log(`[SSE] 🎉 RAW EVENT RECEIVED!`, e);
      try {
        const data: ProcessingEventData = JSON.parse(e.data);
        
        // CRITICAL: Only process events from the CURRENT session
        // Ignore replayed events from previous sessions
        if (data.sessionId !== sessionId) {
          console.log(`[SSE] ⏭️ Skipping event from different session (${data.sessionId} !== ${sessionId})`);
          return; // Skip this event entirely
        }
        
        console.log(`[SSE] ✅ Event received:`, {
          type: data.eventType,
          stage: data.stage,
          progress: `${data.progress}%`,
          message: data.message,
          sessionId: data.sessionId
        });

        if (callbacks.onEvent) {
          callbacks.onEvent(data);
        } else {
          console.warn(`[SSE] ⚠️ No onEvent callback!`);
        }

        // Auto-close on completion or error (we already filtered out other sessions above)
        if (data.eventType === 'complete' || data.eventType === 'error') {
          console.log(`[SSE] Processing ${data.eventType}, closing connection...`);
          
          if (data.eventType === 'complete' && callbacks.onComplete) {
            callbacks.onComplete();
          }

          // Close after a short delay to ensure all messages are processed
          setTimeout(() => {
            this.disconnect(connectionKey);
          }, 1000);
        }
      } catch (error) {
        console.error('[SSE] Error parsing event data:', error);
      }
    });

    // Also listen to default 'message' event as fallback
    eventSource.onmessage = (e: MessageEvent) => {
      console.log(`[SSE] 📨 DEFAULT MESSAGE EVENT:`, e);
    };

    // Handle connection event
    eventSource.addEventListener('connected', (e: MessageEvent) => {
      console.log(`[SSE] 🔗 CONNECTED EVENT RECEIVED!`, e);
      try {
        const data = JSON.parse(e.data);
        console.log(`[SSE] Connection confirmed:`, data.message);
      } catch (error) {
        console.error('[SSE] Error parsing connected event:', error);
      }
    });

    // Handle close event
    eventSource.addEventListener('close', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.log(`[SSE] Server closed connection:`, data.message);
        this.disconnect(connectionKey);
      } catch (error) {
        console.error('[SSE] Error parsing close event:', error);
      }
    });

    // Handle connection open - SET UP AFTER event listeners!
    eventSource.onopen = () => {
      console.log(`[SSE] 🎉 onopen fired! Connected to session: ${sessionId}`);
      console.log(`[SSE] EventSource readyState after open: ${eventSource.readyState}`);
      this.reconnectAttempts.set(connectionKey, 0);
      if (callbacks.onConnect) {
        console.log(`[SSE] Calling onConnect callback...`);
        callbacks.onConnect();
      } else {
        console.warn(`[SSE] ⚠️ No onConnect callback!`);
      }
    };

    // Handle errors
    eventSource.onerror = (error) => {
      console.error(`[SSE] ⚠️ Connection error for session ${sessionId}:`, error);
      console.error(`[SSE] EventSource readyState on error: ${eventSource.readyState} (0=CONNECTING, 1=OPEN, 2=CLOSED)`);
      console.error(`[SSE] Connection key: ${connectionKey}`);

      const attempts = this.reconnectAttempts.get(connectionKey) || 0;

      if (eventSource.readyState === EventSource.CLOSED) {
        console.log(`[SSE] ❌ Connection CLOSED by server for session ${sessionId}`);
        
        // Try to reconnect if under max attempts
        if (attempts < this.MAX_RECONNECT_ATTEMPTS) {
          console.log(`[SSE] Attempting reconnect ${attempts + 1}/${this.MAX_RECONNECT_ATTEMPTS}...`);
          this.reconnectAttempts.set(connectionKey, attempts + 1);
          
          setTimeout(() => {
            this.connect(documentId, sessionId, callbacks);
          }, this.RECONNECT_DELAY);
        } else {
          console.error(`[SSE] Max reconnection attempts reached`);
          if (callbacks.onError) {
            callbacks.onError(new Error('Failed to connect after multiple attempts'));
          }
          this.disconnect(connectionKey);
        }
      }
    };

    // Store connection
    this.connections.set(connectionKey, eventSource);

    return eventSource;
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(connectionKey: string): void {
    const eventSource = this.connections.get(connectionKey);
    if (eventSource) {
      console.log(`[SSE] Disconnecting: ${connectionKey}`);
      eventSource.close();
      this.connections.delete(connectionKey);
      this.reconnectAttempts.delete(connectionKey);
    }
  }

  /**
   * Disconnect all connections
   */
  disconnectAll(): void {
    console.log(`[SSE] Disconnecting all connections (${this.connections.size})`);
    this.connections.forEach((eventSource, key) => {
      eventSource.close();
    });
    this.connections.clear();
    this.reconnectAttempts.clear();
  }

  /**
   * Check if connected
   */
  isConnected(documentId: string, sessionId: string): boolean {
    const connectionKey = `${documentId}_${sessionId}`;
    const eventSource = this.connections.get(connectionKey);
    return eventSource !== undefined && eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Get connection state
   */
  getConnectionState(documentId: string, sessionId: string): number {
    const connectionKey = `${documentId}_${sessionId}`;
    const eventSource = this.connections.get(connectionKey);
    return eventSource?.readyState ?? EventSource.CLOSED;
  }

  /**
   * Get active connections count
   */
  getActiveConnectionsCount(): number {
    return this.connections.size;
  }
}

// Export singleton instance
export const sseService = new SSEService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    sseService.disconnectAll();
  });
}
